import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { ChevronDown, Building2, Plus, CheckCircle, Clock, Edit } from 'lucide-react';
import { useAppStore } from '../stores/appStore';

interface Startup {
  id: string;
  name: string;
  description?: string;
  status: 'draft' | 'in-progress' | 'completed';
  createdAt: Date;
  updatedAt: Date;
  data?: any;
  metrics?: any;
}

export function ProjectSwitcher() {
  const [startups, setStartups] = useState<Startup[]>([]);
  const [currentStartupId, setCurrentStartupId] = useState<string | null>(null);
  const { projectName, setProjectData } = useAppStore();

  // Load startups from localStorage
  useEffect(() => {
    const loadStartups = () => {
      const savedStartups = localStorage.getItem('userStartups');
      if (savedStartups) {
        try {
          const parsed = JSON.parse(savedStartups);
          const startupsWithDates = parsed.map((startup: any) => ({
            ...startup,
            createdAt: new Date(startup.createdAt),
            updatedAt: new Date(startup.updatedAt),
          }));
          setStartups(startupsWithDates);
        } catch (error) {
          console.error('Error loading startups:', error);
        }
      }
    };

    loadStartups();

    // Listen for storage changes to update the list
    const handleStorageChange = () => loadStartups();
    window.addEventListener('storage', handleStorageChange);

    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Determine current startup based on URL or project name
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlStartupId = urlParams.get('startupId');

    if (urlStartupId) {
      setCurrentStartupId(urlStartupId);
      // Sync project name with startup name
      const currentStartup = startups.find(s => s.id === urlStartupId);
      if (currentStartup && currentStartup.name !== projectName) {
        setProjectData({ projectName: currentStartup.name });
      }
    } else if (projectName && startups.length > 0) {
      // Try to find startup by name if no URL param
      const matchingStartup = startups.find(s => s.name === projectName);
      if (matchingStartup) {
        setCurrentStartupId(matchingStartup.id);
      }
    }
  }, [startups, projectName, setProjectData]);

  const getStatusColor = (status: Startup['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'draft':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: Startup['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-3 h-3" />;
      case 'in-progress':
        return <Clock className="w-3 h-3" />;
      case 'draft':
        return <Edit className="w-3 h-3" />;
      default:
        return <Edit className="w-3 h-3" />;
    }
  };

  const handleProjectSwitch = (startup: Startup) => {
    // Update current startup ID
    setCurrentStartupId(startup.id);

    // Sync project name
    setProjectData({ projectName: startup.name });

    // Navigate to the appropriate page
    if (startup.status === 'completed') {
      window.location.href = `/?startupId=${startup.id}`;
    } else {
      window.location.href = `/new?startupId=${startup.id}`;
    }
  };

  const handleCreateNew = () => {
    window.location.href = '/';
  };

  const currentStartup = startups.find(s => s.id === currentStartupId);

  if (startups.length === 0) {
    return (
      <Button variant="outline" size="sm" onClick={handleCreateNew} className="gap-2">
        <Plus className="w-4 h-4" />
        Create Project
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 max-w-[200px]">
          <Building2 className="w-4 h-4" />
          <span className="truncate">
            {currentStartup ? currentStartup.name : 'Select Project'}
          </span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Your Projects</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {startups.map((startup) => (
          <DropdownMenuItem
            key={startup.id}
            onClick={() => handleProjectSwitch(startup)}
            className="flex items-center justify-between p-3"
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{startup.name}</div>
              {startup.description && (
                <div className="text-xs text-muted-foreground truncate">
                  {startup.description}
                </div>
              )}
            </div>
            <Badge className={`gap-1 ml-2 ${getStatusColor(startup.status)}`}>
              {getStatusIcon(startup.status)}
              {startup.status}
            </Badge>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleCreateNew} className="gap-2">
          <Plus className="w-4 h-4" />
          Create New Project
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}